import { ast } from 'peggy';
import { has } from 'lodash';
import { baseTypeMap } from '../utils/constants';
import { Assert, AssertNeedCodeMap, AssertyRuleCode, VisitorRule } from '..';
const parseFiledList = ['expression', 'alternatives', 'elements'];

const parseFiledMap = new Map();

parseFiledList.forEach(field => parseFiledMap.set(field, field));

class ParseRuleTree {
  dependenceMap: Map<string, string> = new Map();
  rules: Array<VisitorRule> = [];
  needCodeMap: AssertNeedCodeMap;
  assert: Assert;
  ruleDependences: AssertNeedCodeMap = new Map();
  constructor(assert: Assert, needCodeMap: AssertNeedCodeMap) {
    this.assert = assert;
    this.needCodeMap = needCodeMap;
  }

  add(rule: VisitorRule) {
    if (!rule) return this;
    this.rules.push(rule);
    return this;
  }

  parse() {
    for (let index = 0; index < this.rules.length; index++) {
      const rule = this.rules[index];
      this.addNeedCodeMap(rule.name);
      const expression = rule.expression;
      if (!expression) {
        // 不存在的rule VisitorRule
        continue;
      }
      if (baseTypeMap.has(expression.type)) {
        continue;
      }
      this.parseRuleExpression(expression);
    }
  }

  parseTheRuleAccordingToField(field: string, rule: VisitorRule) {
    if (field === 'expression') {
      return this.parseRuleExpression(rule.expression);
    }
  }

  parseRuleElements(elements: Array<ast.Element>) {
    for (let index = 0; index < elements.length; index++) {
      const element = elements[index];
      if (element.type === 'rule_ref') {
        if (this.dependenceMap.has(element.name)) {
          continue;
        }
        this.dependenceMap.set(element.name, element.name);
        this.checkRuleType(element.name);
        continue;
      }
      // if (!has(element, 'expression')) {
      //   // 1. base type rule 2. catch rule 3. null rule
      //   continue;
      // }
      this.classificationAnalysis((element as ast.Labeled).expression);
    }
  }

  parseRuleAlternatives(alternatives: Array<ast.Alternative>) {
    for (let index = 0; index < alternatives.length; index++) {
      const alternative = alternatives[index];
      if (alternative.type === 'rule_ref') {
        if (this.dependenceMap.has(alternative.name)) continue;
        this.dependenceMap.set(alternative.name, alternative.name);
        this.checkRuleType(alternative.name);
        continue;
      }
      // if (!has(alternative, 'expression')) {
      //   // rule code | other
      //   continue;
      // }
      this.classificationAnalysis((alternative as ast.Action).expression);
    }
  }

  classificationAnalysis(
    rule: ast.Expression | ast.Named | ast.Alternative | ast.Element
  ) {
    if (rule.type === 'rule_ref') {
      if (this.dependenceMap.has(rule.name)) return false;
      this.dependenceMap.set(rule.name, rule.name);
      this.checkRuleType(rule.name);
      return true;
    }
    if (has(rule, 'expression')) {
      // ast.Action | ast.Labeled | ast.Prefixed | ast.primary? | ast.Repeated | ast.Suffixed
      return this.parseRuleExpression((rule as ast.Action).expression);
    }
    // ast.Choice
    if (has(rule, 'alternatives')) {
      return this.parseRuleAlternatives((rule as ast.Choice).alternatives);
    }
    // ast.Sequence
    if (has(rule, 'elements')) {
      return this.parseRuleElements((rule as ast.Sequence).elements);
    }
  }

  parseRuleExpression(expression: ast.Expression | ast.Named) {
    if (expression.type === 'rule_ref') {
      if (this.dependenceMap.has(expression.name)) return false;
      this.dependenceMap.set(expression.name, expression.name);
      this.checkRuleType(expression.name);
      return true;
    }
    this.classificationAnalysis(expression);
  }

  /**
   * 1. 检查依赖的rule是否是一个复合规则rule
   * 2. 将rule加入到needCodeMap中
   */
  checkRuleType(ruleName: string) {
    this.handleFindRuleData(
      this.findRule(ruleName),
      function handleMap(ruleMap: AssertNeedCodeMap) {
        /**
         * 1. 没有找到
         *    eg:
         *      test = test1111
         *    但是 test1111 rule没有定义，并且没有import引入
         *
         * 2. importDepNeedCodeMap下的
         */
        if (!ruleMap.size) return false;
        for (const [ruleName, value] of ruleMap) {
          if (this.needCodeMap.has(ruleName)) {
            // import的文件的方法和当前文件的方法冲突 ｜ dependence下引入重复的rule
            // todo... 后期加上路径提示
            console.warn(
              `The method of the imported file conflicts with the current file,rule name:${ruleName}`
            );
            continue;
          }
          this.needCodeMap.set(ruleName, value);
        }
      },
      function handleRule(ruleInfo: AssertyRuleCode) {
        if (ruleInfo.isBaseType) {
          this.needCodeMap.set(ruleInfo.ruleName, ruleInfo);
          return false;
        }
        const rules = this.assert.grammer.rules;
        const rule = rules
          .filter(rule => rule.name === ruleInfo.ruleName)
          .pop();
        if (!rule) {
          return false;
        }
        this.rules.push(rule);
      }
    );
  }

  findRule(ruleName: string): AssertNeedCodeMap | AssertyRuleCode {
    const ruleCode = this.assert.ruleCode;
    const importDepNeedCodeMap = this.assert.importDepNeedCodeMap;
    const map: AssertNeedCodeMap = new Map();
    if (ruleCode) {
      const rule = ruleCode.get(ruleName);
      if (rule) {
        return rule;
      }
    }
    // 正常的graphQueue 排序.
    if (importDepNeedCodeMap) {
      const ruleInfo = importDepNeedCodeMap.get(ruleName);
      if (ruleInfo) {
        return ruleInfo;
      }
    }
    return map;
  }

  // findRule方法的数据处理方法
  handleFindRuleData(
    data: AssertNeedCodeMap | AssertyRuleCode,
    mapHandler?: Function,
    ruleHandler?: Function
  ) {
    if (data instanceof Map) {
      mapHandler && mapHandler.call(this, data as AssertNeedCodeMap);
      return true;
    }
    ruleHandler && ruleHandler.call(this, data as AssertyRuleCode);
  }

  addNeedCodeMap(ruleName: string) {
    if (this.needCodeMap.has(ruleName)) return false;
    this.handleFindRuleData(
      this.findRule(ruleName),
      function mapHandler(data: AssertNeedCodeMap) {
        if (!data.size) return false;
        for (const [ruleName, value] of data) {
          this.needCodeMap.set(ruleName, value);
        }
      },
      function ruleHandler(data: AssertyRuleCode) {
        this.needCodeMap.set(data.ruleName, data);
      }
    );
  }
}
export default ParseRuleTree;
